import type { RunStatus } from "@rakazo/contracts";

/** Runs the bot is working on, which the chat shows as working. */
export const WORKING_RUN_STATUSES = [
  "queued",
  "leased",
  "running",
] as const satisfies readonly RunStatus[];
/** Runs paused on the user: an answer, an approval, or a computer takeover. Their card is the state. */
export const WAITING_ON_USER_RUN_STATUSES = [
  "waiting_input",
  "waiting_takeover",
] as const satisfies readonly RunStatus[];
export const ACTIVE_RUN_STATUSES = [
  ...WORKING_RUN_STATUSES,
  ...WAITING_ON_USER_RUN_STATUSES,
] as const satisfies readonly RunStatus[];
/** Runs that hold new work in the queue. A pending card holds no compute. */
export const QUEUE_HOLDING_RUN_STATUSES = ACTIVE_RUN_STATUSES.filter(
  (status) => status !== "waiting_input",
);
/** Runs that have ended. A failed run can still be retried as a new run. */
export const TERMINAL_RUN_STATUSES = [
  "completed",
  "failed",
  "cancelled",
] as const satisfies readonly RunStatus[];
/**
 * These turns carry their own prompt and must not take a user message as steering.
 * Routine and webhook runs are not the conversation, and neither is a wake from another bot:
 * its reply goes back to that bot. The creation intro has no tools; a message that lands
 * during any of them waits, and the continuation after the turn finishes answers it.
 */
export const NON_CONVERSATIONAL_RUN_TRIGGERS = new Set([
  "routine",
  "webhook",
  "created",
  "tickets",
  "bot_message",
  "peer_question",
  "peer_wait",
]);

/**
 * Background triggers do internal work that must not leak into the chat: the
 * sidebar must not show them as activity and the bot transcript must not render
 * their steps/text. Ticket work reports through board comments and its status.
 * Existing ask and approval cards can still request user input.
 */
export const BACKGROUND_RUN_TRIGGERS = ["tickets"] as const;
const BACKGROUND_RUN_TRIGGER_SET = new Set<string>(BACKGROUND_RUN_TRIGGERS);

export function isBackgroundRunTrigger(trigger: string | null | undefined): boolean {
  return BACKGROUND_RUN_TRIGGER_SET.has(trigger ?? "");
}

const allowed: Record<RunStatus, RunStatus[]> = {
  queued: ["leased", "cancelled"],
  leased: ["running", "queued", "cancelled"],
  running: ["waiting_input", "waiting_takeover", "completed", "failed", "cancelled", "leased"],
  waiting_input: ["queued", "leased", "cancelled"],
  waiting_takeover: ["queued", "leased", "cancelled"],
  completed: [],
  failed: ["queued"],
  cancelled: [],
};

export function canTransition(from: RunStatus, to: RunStatus): boolean {
  return allowed[from]?.includes(to) ?? false;
}

export function assertTransition(from: RunStatus, to: RunStatus): void {
  if (!canTransition(from, to)) {
    throw new Error(`Illegal run transition ${from} -> ${to}`);
  }
}

export function isActive(status: RunStatus): boolean {
  return (ACTIVE_RUN_STATUSES as readonly RunStatus[]).includes(status);
}

export function isRunWorking(status: string | null | undefined): boolean {
  return WORKING_RUN_STATUSES.some((working) => working === status);
}

export function isRunWaitingOnUser(status: string | null | undefined): boolean {
  return WAITING_ON_USER_RUN_STATUSES.some((waiting) => waiting === status);
}

/**
 * Work a bot does because another bot woke it. The chat shows the bot working, but the run is not
 * the person's turn: a message sent meanwhile waits for it instead of steering it.
 */
export function isPeerWork(run: { trigger?: string | null; status: string }): boolean {
  return isPeerRunTrigger(run.trigger) && isRunWorking(run.status);
}

export function isTerminal(status: RunStatus): boolean {
  return TERMINAL_RUN_STATUSES.some((terminal) => terminal === status);
}

/** Prompt of a run created to answer messages that waited behind another turn. */
export const STEERING_CONTINUATION_PROMPT = "Respond to the user's steering context.";

/** clientNonce prefix of the interim messages a run posts to the chat while it works. */
export const USER_PROGRESS_CLIENT_NONCE_PREFIX = "user-progress:";
/** Progress slot that marks a message_user answer to the person's interruption. */
export const INTERRUPTION_REPLY_SLOT = "interruption";

/**
 * The person's interruptions a turn has not answered yet, in chat order. Each brief reply answers
 * at most one interruption sent before it, oldest first, so one reply never covers two messages.
 */
export function unansweredInterruptionIds(
  interruptions: ReadonlyArray<{ id: string; seq: number }>,
  replies: ReadonlyArray<{ seq: number }>,
): string[] {
  const timeline: Array<{ seq: number; id?: string }> = [...interruptions, ...replies].sort(
    (a, b) => a.seq - b.seq,
  );
  const pending: string[] = [];
  for (const entry of timeline) {
    if (entry.id !== undefined) pending.push(entry.id);
    else pending.shift();
  }
  return pending;
}

/** Lets that continuation end without a message once the brief reply covered everything. */
export const ANSWERED_INTERRUPTIONS_MAY_END_SILENTLY =
  "If that reply fully answered them and nothing is left to do, the entire final reply must be exactly NO_RESPONSE; do not repeat or rephrase what you already said.";

/**
 * Prompt of the continuation for messages the person sent while the bot was busy, which the bot
 * already answered briefly in passing. Model instruction, not UI copy. Only when every message
 * got that reply may the continuation end silently; otherwise something is still owed.
 */
export function answeredInterruptionsPrompt(
  replies: readonly string[],
  allAnswered: boolean,
): string {
  return [
    STEERING_CONTINUATION_PROMPT,
    `While you were busy with other work you already sent the person this brief reply to ${allAnswered ? "the messages below" : "some of the messages below"}:`,
    replies.join("\n\n"),
    allAnswered
      ? `Do whatever they asked that is still open. ${ANSWERED_INTERRUPTIONS_MAY_END_SILENTLY}`
      : "Do not repeat it. Answer and do whatever else they asked that is still open.",
  ].join("\n\n");
}

/** Whether a run's turn is part of the thread's conversation and may take user steering. */
export function isConversationalRun(trigger: string | null | undefined): boolean {
  return !NON_CONVERSATIONAL_RUN_TRIGGERS.has(trigger ?? "");
}

/**
 * Background turns the person they work for may cut into from the app: the run answers the
 * message at its next model step and carries on. The creation intro has no tools, ticket work
 * cannot post to the chat, and a webhook turn belongs to its external sender, so those keep
 * holding the message until they finish.
 */
export const PERSON_INTERRUPTIBLE_RUN_TRIGGERS: ReadonlySet<string> = new Set([
  "bot_message",
  "routine",
]);

/** Whether the person's own app message goes into this working background run as an interruption. */
export function personMayInterrupt(
  run: { trigger?: string | null; status: string; userId?: string | null },
  senderUserId: string,
): boolean {
  return (
    PERSON_INTERRUPTIBLE_RUN_TRIGGERS.has(run.trigger ?? "") &&
    isRunWorking(run.status) &&
    Boolean(run.userId) &&
    run.userId === senderUserId
  );
}

const WORKING_STATUS_ORDER = ["running", "leased", "queued"];

/**
 * The run a person's message to a busy bot goes to: the conversational turn when there is one,
 * else the background turn the person may interrupt (the one already working first), else the
 * first run holding the queue, which keeps the message until it ends.
 */
export function personMessageTarget<
  Run extends {
    trigger?: string | null;
    status: string;
    userId?: string | null;
    createdAt?: Date;
  },
>(runs: readonly Run[], senderUserId: string): { run: Run; interrupts: boolean } | null {
  const workRuns = runs.filter((run) => !isControlRunTrigger(run.trigger));
  const conversational = workRuns.find((run) => isConversationalRun(run.trigger));
  if (conversational) return { run: conversational, interrupts: false };
  const interruptible = workRuns
    .filter((run) => personMayInterrupt(run, senderUserId))
    .sort(
      (a, b) =>
        WORKING_STATUS_ORDER.indexOf(a.status) - WORKING_STATUS_ORDER.indexOf(b.status) ||
        (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0),
    )[0];
  if (interruptible) return { run: interruptible, interrupts: true };
  const first = workRuns[0];
  return first ? { run: first, interrupts: false } : null;
}

/**
 * Runs a person started and is waiting on. A bot takes them before its queued background work
 * (another bot's wake, a routine, ticket work), whatever order they were created in.
 */
export const PERSON_RUN_TRIGGERS = ["user", "follow_up", "call_end"] as const;

export function nextFence(current: number): number {
  return current + 1;
}

/** Explicit message to the person, distinct from automatic narration. / Явне повідомлення людині. */
export const PERSON_MESSAGE_REPLY_SLOT = "message";

/** Peer replies keep their audience regardless of the execution lane.
 * Відповіді ботам зберігають адресата незалежно від шляху виконання. */
export const PEER_RUN_TRIGGERS = ["bot_message", "peer_question"] as const;
export function isPeerRunTrigger(trigger: string | null | undefined): boolean {
  return PEER_RUN_TRIGGERS.some((value) => value === trigger);
}

/** Model-only communication never gains work tools or a computer.
 * Обмін лише через модель не отримує робочих інструментів чи комп’ютера. */
export const CONTROL_RUN_TRIGGERS = ["peer_wait", "peer_question"] as const;
export function isControlRunTrigger(trigger: string | null | undefined): boolean {
  return CONTROL_RUN_TRIGGERS.some((value) => value === trigger);
}
